import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, MessageSquareText, Send } from 'lucide-react';
import { canAnywhere, type Employee } from '@hrms/shared';
import { Button, Modal, toast } from '@hrms/ui';
import { tsr } from '../../../lib/api.js';
import { PRODUCT_NAME } from '../../../lib/brand.js';
import { copyToClipboard } from '../../../lib/clipboard.js';
import { canComposeSms, openSmsComposer, smsNeedsTap } from '../../../lib/smsComposer.js';
import { shownPhone } from '../../../lib/phoneHint.js';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';

/** Home's own param: sign-up lands here with it when the owner added people from their contacts. */
export const SMS_INVITE_PARAM = 'invite';
export const SMS_INVITE_VALUE = 'sms';

/** The text the owner sends. The link is the one-time set-password link; never logged. */
export function smsInviteText(firstName: string, companyName: string, inviteUrl: string): string {
  const company = companyName.trim() || 'Your company';
  const greeting = firstName.trim() ? `Hi ${firstName.trim()}, ` : 'Hi, ';
  return `${greeting}${company} added you on ${PRODUCT_NAME}. Set up your account: ${inviteUrl}`;
}

/**
 * People the owner still has to text: a mobile number and no email (added from contacts at
 * sign-up, or by hand with only a number), not signed in yet, not deleted, and not the viewer.
 */
export function pendingSmsInvites(
  employees: readonly Employee[],
  selfPersonId: string,
): Employee[] {
  return employees.filter(
    (employee) =>
      !employee.email &&
      Boolean(employee.phone) &&
      employee.signInStatus !== 'active' &&
      !employee.isArchived &&
      employee.personId !== selfPersonId,
  );
}

const SENT_KEY = 'peopleorbit_sms_invites_sent';

/** Rows marked Sent in this browser session: a convenience, so a return from the SMS app keeps them. */
function readSent(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function writeSent(sent: Set<string>) {
  try {
    sessionStorage.setItem(SENT_KEY, JSON.stringify([...sent]));
  } catch {
    // Storage blocked: the rows still show Sent until the page reloads.
  }
}

/** A fetched sign-in link and the number it goes to. Held in memory only; never logged. */
interface InviteLink {
  inviteUrl: string;
  phone: string;
}

/**
 * "Text your team their invite", on Home for whoever can hand out sign-in links: one row per
 * phone-only person who has not signed in, each with "Send SMS". That fetches the person's one-time
 * sign-in link (`getAccountInviteLink`) and opens the owner's own SMS app with the number and the
 * message filled in (lib/smsComposer.ts) — free, no SMS provider; the owner taps Send. The row then
 * shows Sent. On a desktop browser, which has no SMS app, the message is copied instead.
 *
 * Each link fetch makes the person's older links stop working, so a link is fetched once per person
 * while this is mounted and reused (Again sends the same one). A phone browser may block an `sms:`
 * opened after an `await` (smsNeedsTap), so there the tap must already have the link: the sheet
 * from sign-up fetches every row's link when it opens (nobody has been texted yet), and on the card
 * — where a link may already have been texted — the first tap fetches it and the second opens the
 * SMS app.
 *
 * A card while there is anyone to text. Arriving from sign-up (`/?invite=sms`) it opens as a sheet
 * first; closing it drops the param and leaves the card. Renders nothing for anyone else, while
 * loading, or when everyone is in.
 */
export function TeamSmsInvites() {
  const { me, actor } = useAuthenticatedSession();
  const allowed = canAnywhere(actor, 'employee:update');
  const [searchParams, setSearchParams] = useSearchParams();
  const fromSignup = searchParams.get(SMS_INVITE_PARAM) === SMS_INVITE_VALUE;
  const [sent, setSent] = useState<Set<string>>(readSent);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [links, setLinks] = useState<Record<string, InviteLink>>({});
  /** Each person's link request while mounted, so a link is asked for once per person. */
  const requested = useRef(new Map<string, Promise<InviteLink>>());
  /** A phone browser's row whose link the last tap fetched: the next tap opens the SMS app. */
  const [armedId, setArmedId] = useState<string | null>(null);

  const employees = tsr.employees.list.useQuery({
    queryKey: ['employees', 'sms-invites'],
    queryData: { query: { limit: 100 } },
    enabled: allowed,
  });
  const company = tsr.companyProfile.get.useQuery({
    queryKey: ['company-profile'],
    queryData: {},
    enabled: allowed && canAnywhere(actor, 'org.settings:read'),
  });
  const { mutateAsync: requestLink } = tsr.employees.getAccountInviteLink.useMutation();

  const pending =
    allowed && employees.data?.status === 200
      ? pendingSmsInvites(employees.data.body.items, me.person.id)
      : [];
  const prefetchFor = fromSignup && smsNeedsTap() ? pending : [];
  const prefetchKey = prefetchFor.map((employee) => employee.employmentId).join(',');

  /**
   * The person's link: the request already made while mounted (done or in flight), else a new one.
   * A failed request is forgotten, so the next tap asks again.
   */
  function linkFor(employee: Employee): Promise<InviteLink> {
    const id = employee.employmentId;
    const existing = requested.current.get(id);
    if (existing) return existing;
    const request = (async () => {
      const result = await requestLink({ params: { employmentId: id }, body: {} });
      if (result.status !== 200) throw result;
      const phone = result.body.phone ?? employee.phone;
      if (!phone) throw new Error('no phone');
      const link = { inviteUrl: result.body.inviteUrl, phone };
      setLinks((previous) => ({ ...previous, [id]: link }));
      return link;
    })();
    requested.current.set(id, request);
    request.catch(() => requested.current.delete(id));
    return request;
  }

  // The sheet from sign-up in a phone browser: every row's link up front, so Send SMS can open the
  // SMS app within the tap. Rows already marked Sent keep the link they were texted.
  useEffect(() => {
    for (const employee of prefetchFor) {
      if (requested.current.has(employee.employmentId) || sent.has(employee.employmentId)) continue;
      // A failure leaves the row to fetch on its own tap, which shows the reason.
      linkFor(employee).catch(() => {});
    }
    // prefetchKey stands for prefetchFor; linkFor reads only the ref and stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchKey]);

  if (pending.length === 0) return null;
  const companyName = company.data?.status === 200 ? company.data.body.legalName : '';

  function closeSheet() {
    const next = new URLSearchParams(searchParams);
    next.delete(SMS_INVITE_PARAM);
    setSearchParams(next, { replace: true });
  }

  function markSent(employee: Employee) {
    setSent((previous) => {
      const next = new Set(previous).add(employee.employmentId);
      writeSent(next);
      return next;
    });
  }

  async function send(employee: Employee) {
    if (busyId) return;
    const ready = links[employee.employmentId];
    // Opened before any await, so a phone browser counts it as part of the tap.
    if (ready && canComposeSms()) {
      openSmsComposer(ready.phone, smsInviteText(employee.firstName, companyName, ready.inviteUrl));
      setArmedId(null);
      markSent(employee);
      return;
    }
    setBusyId(employee.employmentId);
    try {
      const link = await linkFor(employee);
      if (smsNeedsTap()) {
        // The link is here now; the next tap opens the SMS app (the row reads "Open SMS").
        setArmedId(employee.employmentId);
        return;
      }
      const text = smsInviteText(employee.firstName, companyName, link.inviteUrl);
      if (canComposeSms()) {
        openSmsComposer(link.phone, text);
      } else if (await copyToClipboard(text)) {
        toast.success(
          `Invite for ${employee.firstName} copied. Paste it in a text to ${shownPhone(link.phone)}.`,
        );
      } else {
        toast.error('Open this page on your phone to text the invite.');
        return;
      }
      markSent(employee);
    } catch (error) {
      const message = (error as { body?: { message?: unknown } } | undefined)?.body?.message;
      toast.error(typeof message === 'string' ? message : "Couldn't get the sign-in link.");
    } finally {
      setBusyId(null);
    }
  }

  const list = (
    <ul className="flex flex-col divide-y divide-border">
      {pending.map((employee) => {
        const name =
          employee.displayName ||
          `${employee.firstName} ${employee.lastName}`.trim() ||
          'Team member';
        const done = sent.has(employee.employmentId);
        const armed = !done && armedId === employee.employmentId;
        return (
          <li
            key={employee.employmentId}
            className="flex min-h-11 items-center gap-3 py-2.5"
            data-testid="sms-invite-row"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-fg">{name}</span>
              <span className="block truncate text-xs tabular-nums text-fg-muted">
                {employee.phone ? shownPhone(employee.phone) : ''}
              </span>
            </span>
            {done && (
              <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-success">
                <Check className="size-3.5" aria-hidden="true" />
                Sent
              </span>
            )}
            <Button
              type="button"
              variant={done ? 'ghost' : 'secondary'}
              size="sm"
              loading={busyId === employee.employmentId}
              disabled={busyId !== null && busyId !== employee.employmentId}
              aria-label={done ? `Send SMS to ${name} again` : `Send SMS to ${name}`}
              onClick={() => void send(employee)}
            >
              <Send className="size-3.5" aria-hidden="true" />
              {done ? 'Again' : armed ? 'Open SMS' : 'Send SMS'}
            </Button>
          </li>
        );
      })}
    </ul>
  );

  const intro = (
    <p className="text-sm text-fg-muted">
      Each invite opens your phone&apos;s SMS app with their sign-in link filled in. Tap Send there.
      They set a password and sign in with their mobile number.
    </p>
  );

  return (
    <>
      <section
        aria-labelledby="team-sms-invites-title"
        className="flex min-w-0 flex-col gap-3 rounded-2xl border border-border bg-surface p-4 shadow-xs sm:p-5"
      >
        <header className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent"
          >
            <MessageSquareText className="size-4" />
          </span>
          <div className="min-w-0">
            <h2
              id="team-sms-invites-title"
              className="font-display text-base font-extrabold text-fg"
            >
              Text your team their invite
            </h2>
            <p className="text-sm text-fg-muted">
              {pending.length === 1
                ? '1 person has not signed in yet.'
                : `${pending.length} people have not signed in yet.`}
            </p>
          </div>
        </header>
        {!fromSignup && list}
      </section>

      {fromSignup && (
        <Modal
          open
          title="Text your team their invite"
          onClose={closeSheet}
          showCloseButton
          trapFocus
          sheetOnPhone
        >
          <div className="flex flex-col gap-3">
            {intro}
            {list}
            <div className="flex justify-end">
              <Button variant="primary" onClick={closeSheet}>
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
